/**
 * The studio's own wording for every message it sends (D-166).
 *
 * A-16 put this copy in the database from the start; what was missing was a way
 * for Tonya to reach it, so every wording change was a developer task.
 */

export type NotificationTemplate = {
  templateId: string
  key: string
  name: string
  description: string | null
  emailSubject: string | null
  emailBody: string | null
  smsBody: string | null
  /** The placeholders this message may use. */
  mergeFields: string[]
  isEnabled: boolean
  /** Whether it writes a bell row (D-522). */
  sendsInApp: boolean
  /** Whether it sends an email. */
  sendsEmail: boolean

  /** Whether it also buzzes a phone (D-531). */
  sendsPush: boolean
}

export type TemplateEdit = {
  emailSubject: string | null
  emailBody: string | null
  smsBody: string | null
  isEnabled: boolean
  /**
   * Which channels it uses. <b>Omitted means unchanged</b>, never off — the
   * server reads a missing value as "leave it alone" so that a caller which
   * does not know about channels cannot silence a template by saving (D-522).
   */
  sendsInApp?: boolean
  sendsEmail?: boolean
  sendsPush?: boolean
}

export async function fetchTemplates(): Promise<NotificationTemplate[]> {
  const res = await fetch('/api/admin/notification-templates/')
  if (!res.ok) throw new Error(`Could not load the messages (${res.status})`)
  return res.json()
}

export async function saveTemplate(
  id: string,
  edit: TemplateEdit,
): Promise<NotificationTemplate[]> {
  const res = await fetch(`/api/admin/notification-templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(edit),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not save that (${res.status})`)
  }

  return res.json()
}

/** Every `{{placeholder}}` in a piece of copy, in the order it appears. */
export function placeholders(text: string | null): string[] {
  if (!text) return []
  return [...text.matchAll(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g)].map((m) => m[1])
}

/**
 * Placeholders this message does not know about.
 *
 * Checked as somebody types, because the alternative is finding out when a
 * parent receives an email with `{{dancerName}}` printed in it. The dispatcher
 * deliberately renders an unknown field visibly rather than blanking it — an
 * obviously broken message beats "Hi ," which looks almost right.
 */
export function unknownPlaceholders(
  template: Pick<NotificationTemplate, 'mergeFields'>,
  ...copy: (string | null)[]
): string[] {
  const allowed = new Set(template.mergeFields.map((f) => f.toLowerCase()))

  return [...new Set(copy.flatMap(placeholders))].filter(
    (found) => !allowed.has(found.toLowerCase()),
  )
}

/**
 * A short preview with the placeholders filled in by their own names.
 *
 * Not real data — the point is to show the shape of the sentence, so
 * "Hi {{firstName}}," reads as "Hi [first name]," rather than staying abstract.
 */
export function preview(text: string | null): string {
  if (!text) return ''

  return text.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (_, field: string) =>
    `[${field.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}]`,
  )
}
